import {
  SelectProps as MantineSelectProps,
  Select as MantineSelect,
  ComboboxData,
  Group,
  Text,
  ComboboxLikeRenderOptionInput,
  ComboboxItem,
  Stack,
  Box,
  ComboboxItemGroup,
  MantineSize,
} from "@mantine/core"
import { useUncontrolled } from "@mantine/hooks"
import { IconLock, IconPlus } from "@tabler/icons-react"
import { forwardRef, ReactNode, useEffect, useState } from "react"

import { Label } from "../../../atoms/Label/Label"
import { Tooltip } from "../../../atoms/Tooltip/Tooltip"
import { EqualWidthGrid } from "../../../layout/EqualWidthGrid/EqualWidthGrid"
import { CopyButton } from "../../../molecules/Buttons/CopyButton/CopyButton"
import { Loader } from "../../../molecules/Loader/Loader"

import styles from "./Select.module.css"
import { useSortSelectOptions } from "./useSortSelectOptions"

const DefaultNothingFoundMessage = forwardRef<
  HTMLDivElement,
  { searchValue?: string; label: string; onClick?: (query?: string) => void }
>(({ searchValue, label, onClick }, ref) => (
  <Group
    ref={ref}
    className={styles.createLabel}
    onClick={() => onClick && onClick(searchValue)}
  >
    <IconPlus size={16} height={16} color="var(--mantine-color-primary)" />
    <Text>{searchValue ? `${label} (${searchValue})` : <>{label}</>}</Text>
  </Group>
))

export type SelectOption = ComboboxLikeRenderOptionInput<
  ComboboxItem & {
    disabledTooltip?: string
    locked?: boolean
    lockedTooltip?: string
  }
>

export type SelectProps = {
  creatable?: boolean
  isLoading?: boolean
  createLabel?: string
  onCreate?: (query?: string) => void
  nothingFoundMessage?: string | ReactNode
  copyableValue?: string
  size?: MantineSize
  isAdmin?: boolean
  data?: ComboboxData
  disabled?: boolean
  customRender?: (item: SelectOption) => ReactNode
  errorOnTouch?: boolean
  inlineLabel?: boolean
  sort?:
    | ((
        a: string | ComboboxItem | ComboboxItemGroup,
        b: string | ComboboxItem | ComboboxItemGroup
      ) => number)
    | false
} & Omit<MantineSelectProps, "renderOption">

export const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      data,
      size,
      value,
      isAdmin,
      defaultValue,
      disabled,
      searchValue,
      defaultSearchValue,
      onSearchChange,
      creatable,
      copyableValue,
      createLabel = "Create",
      nothingFoundMessage,
      onCreate,
      customRender,
      onChange,
      isLoading,
      error,
      errorOnTouch = true,
      sort,
      inlineLabel = false,
      label,
      ...props
    },
    ref
  ) => {
    const sortData = useSortSelectOptions({ sort })
    const [selectOptions, setSelectOptions] = useState<
      ComboboxData | undefined
    >(data)
    useEffect(() => {
      const newData =
        data &&
        creatable &&
        !data.some(
          (item) =>
            (typeof item === "object" &&
              "value" in item &&
              item.value === "$create") ||
            item === "$create"
        )
          ? [...data, { value: "$create", label: "$create" }]
          : data
      setSelectOptions(newData)
    }, [data, creatable, sortData])
    const [touched, setTouched] = useState(false)
    const [_value, setValue, controlled] = useUncontrolled({
      value,
      defaultValue,
      finalValue: null,
      onChange,
    })
    const [_searchValue, setSearchValue] = useUncontrolled({
      value: searchValue,
      defaultValue: defaultSearchValue,
      finalValue: getFinalValue(value),
      onChange: onSearchChange,
    })

    const createOption = (
      <Group
        gap="var(--tfg-molecule-padding-x)"
        wrap="nowrap"
        className={styles.createLabel}
      >
        <IconPlus size={16} color="var(--mantine-color-primary-6)" />
        <Text c="primary" fw={700}>
          {createLabel}
        </Text>
      </Group>
    )
    const defaultRenderOption = ({ option, checked }: SelectOption) => {
      if (option.locked || option.disabled) {
        let content: ReactNode | undefined
        if (option.locked) {
          option.disabled = true
          content = (
            <Group
              className={styles.optionRender}
              justify="space-between"
              w="100%"
              c="var(--mantine-color-gray-4)"
              p="var(--tfg-molecule-padding-y)"
              data-checked={checked ? "true" : undefined}
            >
              <Text>{option.label}</Text>
              <IconLock size={12} color="var(--mantine-color-gray-4)" />
            </Group>
          )
        } else if (option.disabled && !option.locked) {
          content = (
            <Box
              className={styles.optionRender}
              p="var(--tfg-molecule-padding-y)"
              data-checked={checked ? "true" : undefined}
              c="var(--mantine-color-gray-4)"
            >
              <Text>{option.label}</Text>
            </Box>
          )
        }
        if (option.disabledTooltip || option.lockedTooltip) {
          return (
            <Tooltip label={option.lockedTooltip ?? option.disabledTooltip}>
              {content}
            </Tooltip>
          )
        }
        return content
      }
      return (
        <Box
          className={styles.optionRender}
          p="var(--tfg-molecule-padding-y)"
          data-checked={checked ? "true" : undefined}
        >
          <Text>{option.label}</Text>
        </Box>
      )
    }

    const renderOption = ({
      option,
      checked,
    }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
      if (option.value === "$create") {
        return createOption
      }
      return customRender
        ? customRender({ option, checked })
        : defaultRenderOption({ option, checked })
    }
    const input = (
      <MantineSelect
        ref={ref}
        data={sortData(selectOptions)}
        size={size}
        label={inlineLabel ? undefined : label}
        classNames={{
          input: styles.input,
          label: styles.label,
        }}
        value={_value}
        onChange={(value, option) => {
          setTouched(true)

          if (value && value !== "$create") {
            setValue(value, option)
            !controlled && setSearchValue(value ?? "")
          } else if (value === "$create") {
            if (
              !data?.some(
                (item) =>
                  typeof item === "object" &&
                  "label" in item &&
                  item.label.toLowerCase() === _searchValue?.toLowerCase()
              )
            ) {
              onCreate?.(_searchValue)
            }
            setSearchValue("")
          }
        }}
        onSearchChange={(value) => {
          setSearchValue(value)
        }}
        onBlur={() => {
          setTouched(true)
        }}
        onClear={() => {
          setTouched(true)
          {
            setValue(null, { value: "", label: "" })
          }
        }}
        nothingFoundMessage={
          nothingFoundMessage ? (
            nothingFoundMessage
          ) : creatable ? (
            <DefaultNothingFoundMessage
              searchValue={_searchValue}
              label={createLabel || `Create (${_searchValue})`}
              onClick={() => {
                onCreate && onCreate(_searchValue)
                setSearchValue("")
              }}
            />
          ) : null
        }
        rightSection={isLoading ? <Loader type="bars" size={10} /> : undefined}
        searchValue={_searchValue}
        renderOption={renderOption}
        error={errorOnTouch ? touched && error : error}
        disabled={disabled}
        aria-sort="ascending"
        {...props}
      />
    )
    const content = (
      <>
        {copyableValue && value && (
          <Group h="fit-content">
            <CopyButton
              label={copyableValue}
              copyableText={value}
              size="xs"
              p={0}
              admin={isAdmin}
              variant={isAdmin ? "admin-tertiary" : "tertiary"}
            />
          </Group>
        )}
        {inlineLabel ? (
          <EqualWidthGrid>
            <Label label={label} fz={size} />
            {input}
          </EqualWidthGrid>
        ) : (
          input
        )}
      </>
    )
    return copyableValue ? (
      <Stack gap="var(--tfg-molecule-padding-y)">{content}</Stack>
    ) : (
      content
    )
  }
)

Select.displayName = "Select"

function getFinalValue(
  value: string | { label: string } | null | undefined
): string {
  return typeof value === "string" ? value : (value?.label ?? "")
}
